import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { NgIf } from '@angular/common';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { AlertBlockComponent } from 'app/components/v2-layout/shared/alert-block/alert-block.component';
import { TwoFactorAuthComponent } from 'app/components/app-layout/settings/user/security/two-factor-auth/two-factor-auth.component';
import { GoogleAuthSetupStepComponent } from 'app/components/v2-layout/shared/two-factor-setup-steps/google-auth-setup-step.component';
import { SmsAuthSetupStepComponent } from 'app/components/v2-layout/shared/two-factor-setup-steps/sms-auth-setup-step.component';
import { MatDialogSizes } from 'app/enums/mat-dialog-sizes.enum';
import { TwoFactorGateReason } from 'app/enums/two-factor-gate-reason.enum';

/** Data contract for {@link TwoFactorGateDialogComponent}. */
export interface TwoFactorGateDialogData
{
    /** Omitted for a generic self-service prompt (e.g. the launchpad card). */
    reason?: TwoFactorGateReason;
}

/** Friendly, reason-specific copy shown at the top of the gate dialog. */
export interface TwoFactorGateCopy
{
    title: string;
    message: string;
}

/**
 * Reason -> copy map. No user-facing strings live in the template; every gate
 * entry point supplies a {@link TwoFactorGateReason} and the matching copy is
 * resolved here.
 */
export const TWO_FACTOR_GATE_COPY: Record<TwoFactorGateReason, TwoFactorGateCopy> = {
    [TwoFactorGateReason.TREASURY_LOGIN]: {
        title: 'Set up two-factor authentication',
        message: 'Your organization uses Financial Accounts, so two-factor authentication is required to protect them. Set up an authenticator app or SMS verification below to continue to your dashboard.',
    },
    [TwoFactorGateReason.TREASURY_KYC]: {
        title: 'Secure your account before Financial Accounts verification',
        message: 'Two-factor authentication is required before you can verify your organization\'s Financial Account. Set up an authenticator app or SMS verification below to continue.',
    },
};

/** Fallback copy for an absent/unknown reason (defensive — reason is typed). */
export const DEFAULT_TWO_FACTOR_GATE_COPY: TwoFactorGateCopy = {
    title: 'Secure your account',
    message: 'Two-factor authentication is required before you can access your organization\'s financial features. Set up an authenticator app or SMS verification below to continue.',
};

/**
 * Reasons that present a HARD gate: the dialog cannot be dismissed (no close
 * button, `disableClose`) and the underlying surface stays withheld until the
 * user enrolls. Everything else is a soft gate the user may back out of.
 */
const HARD_GATE_REASONS: ReadonlySet<TwoFactorGateReason> = new Set([
    TwoFactorGateReason.TREASURY_LOGIN,
]);

/** True when the given reason must present a non-dismissable hard gate. */
export function isHardGateReason(reason: TwoFactorGateReason | undefined): boolean {
    return !!reason && HARD_GATE_REASONS.has(reason);
}

/**
 * Shared, reason-driven two-factor enrollment gate.
 *
 * Wraps the existing standalone {@link TwoFactorAuthComponent} so enrollment is
 * never re-implemented. The hosted component reports enrollment state through
 * its `enrollmentChecked` output; the moment it reports an enrolled
 * authenticator this dialog closes with `true`, so a caller can gate a
 * sensitive action on `afterClosed()` resolving to `true`. Closing without
 * enrolling resolves falsy and the caller must not proceed.
 *
 * The gate hardness is derived from the {@link TwoFactorGateReason}: a
 * TREASURY_LOGIN gate is hard (`disableClose`, no close button — the dashboard
 * is withheld until enrolled), while a TREASURY_KYC gate is dismissable so a
 * user can back out of Treasury setup before verification begins.
 *
 * This is the atomic building block only — trigger wiring (login guard /
 * KYC interception) lives in a separate layer so the enforcement model stays
 * swappable.
 */
@Component({
    selector: 'app-two-factor-gate-dialog',
    templateUrl: './two-factor-gate-dialog.component.html',
    styleUrls: ['./two-factor-gate-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        MatDialogModule,
        AlertBlockComponent,
        TwoFactorAuthComponent,
        GoogleAuthSetupStepComponent,
        SmsAuthSetupStepComponent,
    ],
})
export class TwoFactorGateDialogComponent
{
    /**
     * Open the gate for a given {@link TwoFactorGateReason}. Hard-gate reasons
     * open with `disableClose`. Callers gate their action on the resolved
     * boolean: `open(...).afterClosed()` emits `true` only once the user has
     * finished enrollment.
     */
    static open(
            dialog: MatDialog,
            reason?: TwoFactorGateReason,
    ): MatDialogRef<TwoFactorGateDialogComponent, boolean> {
        return dialog.open<TwoFactorGateDialogComponent, TwoFactorGateDialogData, boolean>(
                TwoFactorGateDialogComponent,
                {
                    // 'v2-dialog' runs resetMatDialog; the 'two-factor-flow-*' classes carry
                    // the v2 surface radius + blurred backdrop globally (shared with the
                    // setup/verify dialogs this gate launches), since the whole flow opens on
                    // the auth layout (body-root CDK overlay) where the v2-layout host's scoped
                    // backdrop/blur never reach. Styled in app.scss.
                    panelClass: ['v2-dialog', 'two-factor-flow-dialog'],
                    backdropClass: 'two-factor-flow-backdrop',
                    width: MatDialogSizes.LG,
                    disableClose: isHardGateReason(reason),
                    data: {reason},
                },
        );
    }

    /** Reason-specific copy, resolved once (OnPush — no template getters). */
    readonly copy: TwoFactorGateCopy;

    /** Whether the user may dismiss the gate (soft gate) vs. must enroll (hard gate). */
    readonly dismissible: boolean;

    /**
     * Which setup step is showing, or null for the method-select (cards) view. Drives the
     * single-surface stepper: the gate swaps its body/footer for the inline setup step rather
     * than opening a nested dialog.
     */
    activeSetup: 'google' | 'sms' | null = null;

    constructor(
            private _dialogRef: MatDialogRef<TwoFactorGateDialogComponent, boolean>,
            @Inject(MAT_DIALOG_DATA) public data: TwoFactorGateDialogData,
    ) {
        const reason = data?.reason;
        this.copy = (reason ? TWO_FACTOR_GATE_COPY[reason] : undefined) ?? DEFAULT_TWO_FACTOR_GATE_COPY;
        this.dismissible = !isHardGateReason(reason);
    }

    /**
     * Handle the hosted component's enrollment report. Close with `true` only
     * once an authenticator is enrolled; a "not enrolled" report is ignored so
     * the user can finish setup.
     */
    onEnrollmentChecked(enrolled: boolean): void {
        if (enrolled) {
            this._dialogRef.close(true);
        }
    }

    /** A method card was tapped — swap the cards for the inline setup step (no nested dialog). */
    onSetupRequested(method: 'google' | 'sms'): void {
        this.activeSetup = method;
    }

    /** Back out of the setup step to the method-select view. */
    onStepCancelled(): void {
        this.activeSetup = null;
    }

    /** Enrollment finished inside the step — the gate's job is done. */
    onStepCompleted(): void {
        this._dialogRef.close(true);
    }
}
